import type { DOMAttributes, DragEvent } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { isWeb } from "tamagui";

import {
  isAcceptedUploadFile,
  type UploadAccept,
} from "@medbillai/utils/files";

export type FileDropHandlers = Pick<
  DOMAttributes<HTMLDivElement>,
  "onDragEnter" | "onDragOver" | "onDragLeave" | "onDrop"
>;

export type UseFileDropResult = {
  isDragActive: boolean;
  dropHandlers: FileDropHandlers;
};

type UseFileDropParams = {
  onFilesDropped: (files: File[]) => void;
  /** Called with the files this zone turned away, so the caller can say why:
   *  a type it can't take, or the ones past `maxFiles` — including every file
   *  when the zone is already full. A wrong type rejects the whole drop
   *  (an upload plus an error reads as two contradictory outcomes for one
   *  gesture); overflow still reports the files that fit. Without it an
   *  unsupported file is silently filtered and the rest report, which is what
   *  a drop into a partially-full slot wants. */
  onFilesRejected?: (rejected: File[]) => void;
  /** What this zone takes. Defaults to `both`. */
  accept?: UploadAccept;
  /** Max files to report from a single drop — the slot's remaining capacity.
   *  Omit when the caller has no per-drop cap and validates the batch's
   *  composition itself. */
  maxFiles?: number;
  disabled?: boolean;
};

/**
 * Web drag-and-drop for a file-request surface; a true no-op on native (and when
 * disabled). It only reports the dropped files — the caller owns the upload.
 */
export const useFileDrop = ({
  onFilesDropped,
  onFilesRejected,
  accept = "both",
  maxFiles,
  disabled = false,
}: UseFileDropParams): UseFileDropResult => {
  const [isDragActive, setIsDragActive] = useState(false);
  // dragenter/leave fire per child element; a depth counter keeps the active
  // state from flickering as the pointer crosses inner nodes.
  const depth = useRef(0);

  const onDragEnter = useCallback((event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    depth.current += 1;
    setIsDragActive(true);
  }, []);

  // preventDefault on dragover is mandatory — without it the browser rejects the
  // drop and onDrop never fires.
  const onDragOver = useCallback((event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  }, []);

  const onDragLeave = useCallback((event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    depth.current -= 1;
    if (depth.current <= 0) {
      depth.current = 0;
      setIsDragActive(false);
    }
  }, []);

  const onDrop = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      depth.current = 0;
      setIsDragActive(false);
      const dropped = Array.from(event.dataTransfer.files);
      const rejected = dropped.filter(
        file =>
          !isAcceptedUploadFile(
            { name: file.name, contentType: file.type },
            accept,
          ),
      );
      if (onFilesRejected && rejected.length > 0) {
        onFilesRejected(rejected);
        return;
      }
      // Slice to the slot's remaining capacity so a multi-file drop can't push
      // past maxFiles — the same cap the add button and drop-disable enforce.
      // Undefined means uncapped, for callers that validate the batch instead.
      const accepted = dropped.filter(file => !rejected.includes(file));
      const toReport = accepted.slice(0, maxFiles);
      const overflow = accepted.slice(toReport.length);
      if (onFilesRejected && overflow.length > 0) {
        onFilesRejected(overflow);
      }
      if (toReport.length === 0) {
        return;
      }
      onFilesDropped(toReport);
    },
    [accept, maxFiles, onFilesDropped, onFilesRejected],
  );

  // A drag in flight when the slot is disabled would otherwise leave the active
  // highlight stuck for the next re-enable; clear the bookkeeping on disable.
  useEffect(() => {
    if (disabled) {
      depth.current = 0;
      setIsDragActive(false);
    }
  }, [disabled]);

  if (!isWeb || disabled) {
    return { isDragActive: false, dropHandlers: {} };
  }

  return {
    isDragActive,
    dropHandlers: { onDragEnter, onDragOver, onDragLeave, onDrop },
  };
};
